import { FormikProps } from "formik";
import React, { useEffect, useState } from "react";
import {
    Form,
    AppSection,
    InputText,
    ActionList,
    ButtonPrimary,
    AppContent,
    useSnackbar,
    AppSectionHeader,
    InputSwitch,
    NotificationSettingsFieldGroup,
    AppFooter,
    Button,

} from '../../components-v2/shared';
import { returnAsArray, formatValues } from "./LiveChat.helpers";
import { FormikHelpers } from 'formik';
import { ILiveChat } from "../../types";
import { LivechatValidationSchema } from "./LiveChat.validations";


export const CallToAction = ({ liveChat, companyId, active, setActivateWidgetToggle }) => {
    const { addSnackbar } = useSnackbar();
    const livechatItem = returnAsArray(liveChat.values.length ? liveChat.values[0] : null)[0] || null;
    const [activeStandalone, setActiveStandalone] = useState<boolean>(livechatItem?.show_as_standalone ?? false);
    const [showOnTop, setShowOnTop] = useState<boolean>(livechatItem?.show_on_top ?? false);

    useEffect(() => {
        setActiveStandalone(livechatItem?.show_as_standalone ?? false);
        setShowOnTop(livechatItem?.show_on_top ?? false);
    }, [livechatItem]);

    const getInitialValues = () => {
        const defaultValues: Partial<ILiveChat> = {
            company_id: Number(companyId),
            active: false,
            show_as_standalone: false,
            show_on_top: false,
            call_to_action: '',
            override_lead_notification: false,
            crm_email: '',
            lead_notification_primary_emails: '',
            lead_notification_cc_emails: '',
            reply_to_email: '',
            phone: '',
            crm_config_id: null
        };

        if (!livechatItem) {
            return defaultValues as ILiveChat;
        }

        if (!livechatItem.call_to_action) {
            return {
                ...defaultValues,
                ...livechatItem,
                call_to_action: ''
            } as ILiveChat;
        }

        return {
            ...defaultValues,
            ...livechatItem
        } as ILiveChat;
    };

    const handleSubmit = async (values: ILiveChat, actions: FormikHelpers<ILiveChat>) => {
        try {
            await liveChat.updateChatWidget(formatValues(values, companyId));
            setActivateWidgetToggle(true);
            addSnackbar('Livechat has been updated.', {
                variant: 'success'
            });
        } catch (error) {
            actions.setErrors(error.response.data);
            addSnackbar('Failed to update Livechat.', {
                variant: 'error'
            });
        }
    };

    const handleStandaloneToggle = () => {
        setActiveStandalone(previousValue => !previousValue);
    };

    return (
        <Form
            enableReinitialize
            initialValues={getInitialValues()}
            onSubmit={handleSubmit}
            validationSchema={LivechatValidationSchema}
            confirmUnsavedChanges
            unsavedChangesConfig={{}}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {(formikProps: FormikProps<ILiveChat>) => {
                return (
                    <AppSection>
                        <InputText formikProps={formikProps} name="call_to_action" label="Call to Action" />
                        <AppContent className="field-group-content">
                            <div className="field-group-col">
                                <NotificationSettingsFieldGroup formikProps={formikProps} crmBlacklist={[]} />
                            </div>
                            <div className="field-group-col" style={{
                                backgroundColor: 'rgb(245, 246, 250)',
                                padding: '20px',
                                borderRadius: '20px'
                            }}>
                                <AppSectionHeader title="Enable Standalone Widget" >
                                    <InputSwitch
                                        labelOn="Enabled"
                                        labelOff="Disabled"
                                        labelPlacement="start"
                                        formikProps={formikProps}
                                        name={'show_as_standalone'}
                                        checked={activeStandalone}
                                        onClick={handleStandaloneToggle}
                                        disabled={!active}
                                    />
                                </AppSectionHeader>

                                <AppSectionHeader title="Show Widget On Top" >
                                    <InputSwitch
                                        labelOn="Enabled"
                                        labelOff="Disabled"
                                        labelPlacement="start"
                                        formikProps={formikProps}
                                        name={'show_on_top'}
                                        checked={showOnTop}
                                        onClick={() => {
                                            setShowOnTop(previousValue => !previousValue);
                                        }}
                                        disabled={!active}
                                    />
                                </AppSectionHeader>
                            </div>
                        </AppContent>

                        <AppFooter>
                            <ActionList position="end">
                                {formikProps.dirty && (
                                    <Button
                                        onClick={() => formikProps.resetForm()}
                                        disabled={formikProps.isSubmitting}
                                        data-lc-trigger-unsaved-changes={true}
                                        data-test-id="button_cancel"
                                    >
                                        Cancel
                                    </Button>
                                )}
                                <ButtonPrimary
                                    type="submit"
                                    disabled={!formikProps.dirty || formikProps.isSubmitting}
                                    data-test-id="button_save"
                                >
                                    {formikProps.isSubmitting ? 'Saving...' : 'Save'}
                                </ButtonPrimary>
                            </ActionList>
                        </AppFooter>
                    </AppSection>
                );
            }}
        </Form>
    )
}

